import appStore from "app/mobxStore/AppStore";

interface RequestOptions {
  headers?: { [key: string]: string };
  body?: any;
  // Skip the response cache and in-flight sharing (e.g. a Refresh button).
  fresh?: boolean;
}

// POST endpoints that only read data, so identical calls can be shared.
const READ_ONLY_POSTS = [
  /^user-details$/,
  /^getobject$/,
  /^get-application-id$/,
  /^get\/filedata$/,
  /^get\/file$/,
  /^check-task-access$/,
  /^tasks\/current-tasks$/,
  /^document\/object-by-task$/,
  /^user-dashboard$/,
  /^outstainding-tasks$/,
  /^dynamic-dashboard$/,
  /^control-center-filter-options$/,
  /^search-document-table$/,
];

// Live data: share identical in-flight calls, but never serve from cache.
const NEVER_CACHE = [/^chat\//, /online-users/, /^task\/notes\//, /^notification/];

// Reference data that rarely changes. Any create/update/delete request
// clears the whole cache, so edits made in this app show up straight away.
const LONG_CACHE = [
  /^get-user-entities\//,
  /^user\/document-types\//,
  /^entities$/,
  /^tasks\/\d+\/group-index-type$/,
  /^administrator\/document-group-types$/,
  /^administrator\/database-tables$/,
  /^administrator\/roles\/names\/get$/,
  /^administrator\/types\/role-type\/get$/,
  /^administrator\/options\//,
  /^administrator\/document-types$/,
  /^administrator\/document-groups$/,
  /^administrator\/forms$/,
  /^administrator\/workflows$/,
  /^administrator\/user-settings-types$/,
  /^administrator\/newsletters\/types$/,
  /^document-icons$/,
];

// Collapses bursts: components mounting together, effects firing twice.
const SHORT_CACHE_MS = 2000;
const LONG_CACHE_MS = 5 * 60 * 1000;

const getPath = (url: string) => url.split("?")[0].replace(/^\/+/, "");

const isReadRequest = (method: string, url: string) =>
  method === "GET" ||
  (method === "POST" && READ_ONLY_POSTS.some((pattern) => pattern.test(getPath(url))));

const getCacheTtl = (url: string) => {
  const path = getPath(url);
  if (NEVER_CACHE.some((pattern) => pattern.test(path))) return 0;
  if (LONG_CACHE.some((pattern) => pattern.test(path))) return LONG_CACHE_MS;
  return SHORT_CACHE_MS;
};

// Every caller gets its own copy, so one component mutating a response
// can't change what another component (or the cache) sees.
const cloneResponse = <T,>(data: T): T => {
  if (data === null || typeof data !== "object") return data;
  try {
    return typeof structuredClone === "function"
      ? structuredClone(data)
      : JSON.parse(JSON.stringify(data));
  } catch {
    return data;
  }
};

class FetchAPI {
  private baseUrl: string;
  private loading: boolean;
  private error: string | null;
  private authToken: string | null;
  private navigations: any[];
  private inFlight = new Map<string, Promise<any>>();
  private cache = new Map<string, { expires: number; data: any }>();
  // Bumped whenever the cache is cleared, so a read that started before a
  // write can't put pre-write data back into the cache.
  private cacheGeneration = 0;

  constructor(baseUrl: string, authToken: string | null = null) {
    this.baseUrl = baseUrl;
    this.loading = false;
    this.error = null;
    this.authToken = authToken;
  }

  public clearCache(): void {
    this.cache.clear();
    this.inFlight.clear();
    this.cacheGeneration += 1;
  }

  private async request(
    url: string,
    method: string,
    options?: RequestOptions
  ): Promise<any> {
    if (!isReadRequest(method, url)) {
      this.clearCache();
      try {
        return await this.send(url, method, options);
      } finally {
        this.clearCache();
      }
    }

    const key = `${method} ${url} ${
      options?.body === undefined ? "" : JSON.stringify(options.body)
    }`;
    const ttl = getCacheTtl(url);

    if (!options?.fresh) {
      const cached = this.cache.get(key);
      if (cached && cached.expires > Date.now()) {
        return cloneResponse(cached.data);
      }
      const pending = this.inFlight.get(key);
      if (pending) {
        return pending.then(cloneResponse);
      }
    }

    const generation = this.cacheGeneration;
    const promise = this.send(url, method, options);
    this.inFlight.set(key, promise);
    try {
      const data = await promise;
      if (ttl > 0 && generation === this.cacheGeneration) {
        this.cache.set(key, { expires: Date.now() + ttl, data });
      }
      return cloneResponse(data);
    } finally {
      if (this.inFlight.get(key) === promise) {
        this.inFlight.delete(key);
      }
    }
  }

  private async send(
    url: string,
    method: string,
    options?: RequestOptions
  ): Promise<any> {
    try {
      appStore.setLoading(true);
      this.error = null;

      const headers: { [key: string]: string } = {
        "Content-Type": "application/json",
        ...options?.headers,
      };

      if (this.authToken) {
        headers["Authorization"] = `Bearer ${this.authToken}`;
      }

      const response = await fetch(`${this.baseUrl}${url}`, {
        method,
        headers,
        body: JSON.stringify(options?.body),
      });

      const data = await response.json();
      this.loading = false;
      appStore.setLoading(false);
      if (!response.ok) {
        this.error = data.message || "Something went wrong.";
        throw new Error(this.error);
      }

      return data;
    } catch (error) {
      this.loading = false;
      appStore.setLoading(false);
      const message =
        typeof (error as any)?.message === "string"
          ? (error as any).message
          : "Something went wrong.";
      this.error = message;
      throw error;
    }
  }

  public async get(url: string, options?: RequestOptions): Promise<any> {
    return this.request(url, "GET", options);
  }

  public async post(url: string, options?: RequestOptions): Promise<any> {
    return this.request(url, "POST", options);
  }

  public async put(url: string, options?: RequestOptions): Promise<any> {
    return this.request(url, "PUT", options);
  }

  public async delete(url: string, options?: RequestOptions): Promise<any> {
    return this.request(url, "DELETE", options);
  }

  public isLoading(): boolean {
    return this.loading;
  }

  public getError(): string | null {
    return this.error;
  }

  public setAuthToken(token: string | null): void {
    if (token !== this.authToken) {
      // Responses belong to the previous user/session.
      this.clearCache();
    }
    this.authToken = token;
  }
  public setNavigations(navigations: []): void {
    this.navigations = navigations;
  }
  public setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
  }
  public getBaseUrl(): string {
    return this.baseUrl;
  }
}

const getInitialBaseUrl = (): string =>
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5200/api/";

export const api = new FetchAPI(getInitialBaseUrl());
